<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="420px"
    content-class="blog-verify-dialog"
    persistent
  >
    <div class="blog-verify-modal">
      <div class="blog-verify-header">
        <div class="blog-verify-icon">
          <i class="mdi mdi-shield-lock-outline"></i>
        </div>
        <h2 class="blog-verify-title">
          Verify to Edit
        </h2>
        <p class="blog-verify-description">
          Only the author of this post or a Super Admin
          <br>
          can make changes.
        </p>
      </div>

      <form class="blog-verify-body" @submit.prevent="handleVerify">
        <div class="blog-verify-field">
          <label>Username</label>
          <div class="blog-verify-input-wrap">
            <i class="mdi mdi-account-outline"></i>
            <input
              v-model="username"
              type="text"
              placeholder="Enter your username"
              autocomplete="username"
              :disabled="loading"
            />
          </div>
        </div>

        <div class="blog-verify-field">
          <label>Password</label>
          <div class="blog-verify-input-wrap">
            <i class="mdi mdi-lock-outline"></i>
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="Enter your password"
              autocomplete="current-password"
              :disabled="loading"
            />
            <i
              class="mdi blog-verify-toggle-pw"
              :class="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              @click="showPassword = !showPassword"
            ></i>
          </div>
        </div>

        <div class="blog-verify-field">
          <label>Calculate This Operation</label>

          <div class="blog-verify-captcha-wrap">
            <canvas ref="captchaCanvas" width="150" height="60"></canvas>

            <button
              type="button"
              class="blog-verify-captcha-refresh"
              :disabled="loading"
              @click="generateCaptcha"
              title="Refresh CAPTCHA"
            >
              <i class="mdi mdi-refresh"></i>
            </button>
          </div>

          <div class="blog-verify-input-wrap">
            <i class="mdi mdi-shield-check-outline"></i>
            <input
              v-model="captchaInput"
              type="text"
              placeholder="Enter CAPTCHA answer"
              :disabled="loading"
            />
          </div>
        </div>

        <transition name="fade">
          <div v-if="errorMessage" class="blog-verify-error">
            <i class="mdi mdi-alert-circle-outline"></i>
            <span>{{ errorMessage }}</span>
          </div>
        </transition>

        <div class="blog-verify-actions">
          <button
            type="button"
            class="blog-verify-btn blog-verify-btn-cancel"
            :disabled="loading"
            @click="handleCancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="blog-verify-btn blog-verify-btn-submit"
            :disabled="loading || !username || !password || !captchaInput"
          >
            <i v-if="loading" class="mdi mdi-loading mdi-spin"></i>
            <i v-else class="mdi mdi-check-circle-outline"></i>
            {{ loading ? 'Verifying...' : 'Verify' }}
          </button>
        </div>
      </form>
    </div>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import { decryptionData } from '@/components/utils/utils.js';

export default {
  name: 'BlogAuthVerifyDialog',
  props: {
    value: { type: Boolean, default: false },
    requiredAuthor: { type: String, default: null }
  },
  data() {
    return {
      username: '',
      password: '',
      showPassword: false,
      loading: false,
      errorMessage: '',
      captchaText: '',
      captchaAnswer: null,
      captchaInput: ''
    };
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.resetForm();
    }
  },
  mounted() {
    this.generateCaptcha();
  },
  methods: {
    resetForm() {
      this.username = '';
      this.password = '';
      this.showPassword = false;
      this.loading = false;
      this.errorMessage = '';
      this.captchaInput = '';

      this.$nextTick(() => {
        this.generateCaptcha();
      });
    },
    generateCaptcha() {
      const num1 = Math.floor(Math.random() * 10) + 1;
      const num2 = Math.floor(Math.random() * 10) + 1;

      const operators = ['+', '-', 'x'];
      const operator = operators[Math.floor(Math.random() * operators.length)];

      let answer;
      switch (operator) {
        case '+':
          answer = num1 + num2;
          break;
        case '-':
          answer = num1 - num2;
          break;
        case 'x':
          answer = num1 * num2;
          break;
      }
      this.captchaAnswer = answer;
      this.captchaText = `${num1} ${operator} ${num2}`;

      const canvas = this.$refs.captchaCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = '#e0e0e0';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = '26px Arial';
      ctx.fillStyle = '#333';
      ctx.setTransform(1, 0.1, -0.1, 1, 0, 0);

      ctx.fillText(this.captchaText, 20, 32);

      for (let i = 0; i < 5; i++) {
        ctx.strokeStyle = '#999';
        ctx.beginPath();
        ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.stroke();
      }
    },
    handleCancel() {
      this.$emit('input', false);
    },
    async handleVerify() {
      this.errorMessage = '';

      if (Number(this.captchaInput) !== this.captchaAnswer) {
        this.errorMessage = 'Incorrect CAPTCHA. Please try again.';
        this.generateCaptcha();
        this.captchaInput = '';
        return;
      }

      this.loading = true;

      try {
        const response = await axios.post('login', {
          username: this.username,
          password: this.password
        });

        const decrypted = decryptionData(response.data.token);
        const groupName = decrypted?.group?.GROUP_NAME || '';
        const loggedInUsername = decrypted?.username || '';

        const isSuperAdmin = groupName === 'Super Admin';
        const isSameAuthor = this.requiredAuthor && loggedInUsername === this.requiredAuthor;

        const token = decrypted.token;

        if (isSuperAdmin || isSameAuthor) {
          this.$emit('verified', {
            username: loggedInUsername,
            groupName,
            token
          });
          this.$emit('input', false);
        } else {
          this.errorMessage = 'You are not authorized to edit this blog post.';
          this.generateCaptcha();
          this.captchaInput = '';
        }
      } catch (err) {
        this.errorMessage = err?.response?.data?.msg || 'Invalid username or password.';
        this.generateCaptcha();
        this.captchaInput = '';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.blog-verify-dialog {
  border-radius: 18px !important;
  overflow: hidden;
  box-shadow:
    0 24px 80px rgba(0, 29, 108, 0.18),
    0 8px 30px rgba(33, 39, 42, 0.12);
}

.blog-verify-modal {
  background: #ffffff;
  border-radius: 18px;
  overflow: hidden;
}

.blog-verify-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 28px 22px;
  background: linear-gradient(180deg, #f2f4f8 0%, #ffffff 100%);
  border-bottom: 1px solid #e4e7eb;
}

.blog-verify-icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  border-radius: 16px;
  background: #001d6c;
  color: #ffffff;
  box-shadow: 0 8px 20px rgba(0, 29, 108, 0.25);
}

.blog-verify-icon i {
  font-size: 27px;
}

.blog-verify-title {
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 750;
  color: #21272a;
}

.blog-verify-description {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #697077;
  max-width: 300px;
}

.blog-verify-body {
  padding: 18px 28px 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.blog-verify-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.blog-verify-field label {
  font-size: 13px;
  font-weight: 700;
  color: #4d5358;
}

.blog-verify-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.blog-verify-input-wrap > i:first-child {
  position: absolute;
  left: 13px;
  font-size: 18px;
  color: #a2a9b0;
  pointer-events: none;
}

.blog-verify-input-wrap input {
  width: 100%;
  height: 44px;
  padding: 0 13px 0 40px;
  border: 1px solid #dfe3e6;
  border-radius: 10px;
  background: #ffffff;
  color: #21272a;
  font-family: inherit;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.blog-verify-input-wrap input::placeholder {
  color: #a2a9b0;
}

.blog-verify-input-wrap input:focus {
  border-color: #8fa9d6;
  box-shadow: 0 0 0 3px rgba(0, 29, 108, 0.07);
}

.blog-verify-input-wrap input:disabled {
  background: #f7f8fa;
  cursor: not-allowed;
}

.blog-verify-toggle-pw {
  position: absolute;
  right: 13px;
  font-size: 18px;
  color: #a2a9b0;
  cursor: pointer;
}

.blog-verify-toggle-pw:hover {
  color: #4d5358;
}

.blog-verify-captcha-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.blog-verify-captcha-wrap canvas {
  border: 1px solid #dfe3e6;
  border-radius: 8px;
  background: #e0e0e0;
}

.blog-verify-captcha-refresh {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dfe3e6;
  border-radius: 8px;
  background: #ffffff;
  color: #4d5358;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease;
}

.blog-verify-captcha-refresh:hover:not(:disabled) {
  background: #f2f4f8;
  color: #001d6c;
  transform: rotate(60deg);
}

.blog-verify-captcha-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.blog-verify-captcha-refresh i {
  font-size: 18px;
}

.blog-verify-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 13px;
  border-radius: 9px;
  background: #fdecea;
  border: 1px solid #f4c6c1;
  color: #b3261e;
  font-size: 13px;
  font-weight: 600;
}

.blog-verify-error i {
  font-size: 17px;
  flex-shrink: 0;
}

.blog-verify-actions {
  display: flex;
  gap: 10px;
  margin-top: 4px;
}

.blog-verify-btn {
  flex: 1;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.blog-verify-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.blog-verify-btn-cancel {
  border: 1px solid #dfe3e6;
  background: #ffffff;
  color: #4d5358;
}

.blog-verify-btn-cancel:hover:not(:disabled) {
  background: #f7f8fa;
  color: #21272a;
}

.blog-verify-btn-submit {
  border: 1px solid #111827;
  background: #111827;
  color: #ffffff;
  box-shadow: 0 3px 8px rgba(17, 24, 39, 0.14);
}

.blog-verify-btn-submit:hover:not(:disabled) {
  background: #1f2937;
  transform: translateY(-1px);
  box-shadow: 0 5px 12px rgba(17, 24, 39, 0.18);
}

.blog-verify-btn-submit i {
  font-size: 16px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>